<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Skeleton from 'primevue/skeleton'
import StatCard from '@/components/ui/StatCard.vue'
import { useAuthStore } from '@/stores/auth'
import { useNavigation } from '@/composables/useNavigation'
import { summaryApi, type HomeSummary } from '@/services/summary'
import { formatShortDate } from '@/utils/format'

/**
 * Pantalla de inicio.
 *
 * Tres bloques: saludo, accesos rápidos y las cuatro cifras del mes. Las cifras
 * vienen calculadas del backend con el ámbito ya decidido por el rol, y aquí solo
 * se formatean: recalcular un total en el cliente sería tener dos reglas para lo
 * mismo y dos respuestas distintas a «de dónde sale este número».
 *
 * Carga, error y «no hay nada» son tres estados distintos. Un cero sin contexto
 * parece un fallo de cálculo, y un fallo de red pintado como cero hace que el
 * usuario repita acciones que ya funcionaban.
 */

const router = useRouter()
const authStore = useAuthStore()
const { navItems, canSee } = useNavigation()

const summary = ref<HomeSummary | null>(null)
const loading = ref(false)
const errorMessage = ref<string | null>(null)

/**
 * Accesos rápidos, derivados de la navegación.
 *
 * Se construyen a partir de `navItems` y no se escriben aparte para que un acceso
 * rápido nunca apunte a una sección que el usuario no puede ver. Antes era una
 * lista propia que se olvidaba de actualizar cuando cambiaba un permisos.
 */
const shortcuts = computed(() =>
  navItems.value
    .filter((item) => item.route !== 'home' && canSee(item))
    .map((item) => ({
      label: item.label,
      icon: item.icon,
      route: item.route,
    })),
)

/**
 * Las cuatro cifras, con su icono y su color.
 *
 * `new_customers` se filtra cuando el backend devuelve `null`: sin autor en la
 * ficha de cliente no se puede acotar al asesor, y un `—` es más honesto que un
 * cero que parecería que no se ha dado de alta ningún cliente.
 */
const stats = computed(() => {
  if (summary.value === null) return []

  const { quotes_month, invoiced_documents_month, new_customers, pending_follow_ups, range } = summary.value
  const delMes = `Del ${formatShortDate(range.from)} al ${formatShortDate(range.to)}`

  const cards: Array<{
    key: string
    label: string
    value: string
    context: string
    icon: string
    tone: 'brand' | 'success' | 'info' | 'warn' | 'danger' | 'neutral'
    /** Sin `to`, la tarjeta no es pulsable. «Facturadas» lo necesita porque
     *  suma cotizaciones y tienda, y no hay un listado único al que llevar. */
    to?: string
  }> = [
    {
      key: 'quotes',
      label: 'Cotizaciones del mes',
      value: String(quotes_month),
      context: delMes,
      icon: 'pi pi-file',
      tone: 'brand',
      to: 'quotes.index',
    },
    {
      key: 'invoiced',
      label: 'Facturadas',
      value: String(invoiced_documents_month),
      context: 'Cotizaciones y ventas tienda',
      icon: 'pi pi-check-circle',
      tone: 'success',
    },
    {
      key: 'followups',
      label: 'Seguimientos pendientes',
      value: String(pending_follow_ups),
      context: 'Vencidos y los de hoy',
      icon: 'pi pi-bell',
      tone: pending_follow_ups > 0 ? 'warn' : 'neutral',
      to: 'followups.index',
    },
  ]

  if (new_customers !== null) {
    cards.splice(2, 0, {
      key: 'customers',
      label: 'Clientes nuevos',
      value: String(new_customers),
      context: delMes,
      icon: 'pi pi-user-plus',
      tone: 'info',
      to: 'customers.index',
    })
  }

  return cards
})


function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el resumen. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await summaryApi.get()
    summary.value = data.data
  } catch (cause: unknown) {
    summary.value = null
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="mx-auto w-full max-w-screen-xl space-y-8 animate-fade-in">
    <!--
      Solo el saludo.

      Aquí había una tarjeta con el avatar, el nombre de usuario y el rol. El botón de
      perfil de la cabecera ya lleva el avatar, el nombre y el rol, así que la tarjeta
      repetía en otra parte lo que se está leyendo dos pantallas más arriba: el nombre
      en el saludo de este mismo encabezado y el avatar en la cabecera. Un dato repetido
      no está en dos sitios por casualidad: está en dos sitios para que los dos se
      puedan quedar viejos.

      Lo que sí se pierde aquí es el **nombre de usuario**, que ese botón no lleva, y el
      rol por debajo de 768 px, donde el botón se queda en la inicial. Los dos siguen en
      «Mi perfil», que es donde se consultan.
    -->
    <header>
      <div class="min-w-0">
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Hola, {{ authStore.user?.name ?? 'usuario' }}
        </h1>
        <p class="mt-1 text-sm text-surface-500 dark:text-surface-400">
          {{ authStore.canAccessDashboard ? 'Panel de control de la empresa' : 'Tu actividad' }}
        </p>
      </div>
    </header>

    <!-- Cifras del mes -->
    <section aria-labelledby="inicio-cifras">
      <div class="mb-4 flex items-baseline justify-between gap-4">
        <h2
          id="inicio-cifras"
          class="text-lg font-semibold text-surface-900 dark:text-surface-100"
        >
          {{ summary?.scope === 'company' ? 'Este mes, en la empresa' : 'Este mes, lo tuyo' }}
        </h2>
        <p
          v-if="summary"
          class="shrink-0 text-sm text-surface-500 dark:text-surface-400"
        >
          Del {{ formatShortDate(summary.range.from) }} al {{ formatShortDate(summary.range.to) }}
        </p>
      </div>

      <!-- Error: separado del «no hay cifras», que es un estado distinto. -->
      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-6 py-10 text-center dark:border-amber-900/60 dark:bg-amber-950/30"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudo cargar el resumen
        </p>
        <p class="max-w-md text-sm text-surface-600 dark:text-surface-300">
          {{ errorMessage }}
        </p>
        <button
          type="button"
          class="rounded-lg border border-surface-300 bg-white px-4 py-2 text-sm font-medium text-surface-700 transition-colors hover:bg-surface-50 dark:border-surface-600 dark:bg-surface-800 dark:text-surface-200 dark:hover:bg-surface-700"
          @click="load"
        >
          Reintentar
        </button>
      </div>

      <div
        v-else-if="loading"
        class="grid grid-cols-2 gap-4 lg:grid-cols-4"
        aria-busy="true"
      >
        <div
          v-for="index in 4"
          :key="index"
          class="rounded-xl border border-surface-200 bg-white p-5 dark:border-surface-700 dark:bg-surface-900"
        >
          <Skeleton
            width="2.5rem"
            height="2.5rem"
            class="rounded-lg"
          />
          <Skeleton
            width="3rem"
            height="2rem"
            class="mt-4"
          />
          <Skeleton
            width="70%"
            class="mt-2"
          />
        </div>
      </div>

      <div
        v-else-if="stats.length > 0"
        class="grid grid-cols-2 gap-4 lg:grid-cols-4"
      >
        <StatCard
          v-for="stat in stats"
          :key="stat.key"
          :label="stat.label"
          :value="stat.value"
          :context="stat.context"
          :icon="stat.icon"
          :tone="stat.tone"
          :to="stat.to"
        />
      </div>

      <p
        v-else
        class="rounded-xl border border-dashed border-surface-300 px-6 py-10 text-center text-sm text-surface-500 dark:border-surface-600 dark:text-surface-400"
      >
        Todavía no hay cifras que enseñar.
      </p>
    </section>

    <!-- Accesos rápidos -->
    <section aria-labelledby="inicio-accesos">
      <h2
        id="inicio-accesos"
        class="mb-4 text-lg font-semibold text-surface-900 dark:text-surface-100"
      >
        Accesos rápidos
      </h2>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <button
          v-for="shortcut in shortcuts"
          :key="shortcut.route"
          type="button"
          class="group flex flex-col items-center gap-3 rounded-xl border border-surface-200 bg-white px-4 py-6 text-center transition-all hover:border-primary-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:border-surface-700 dark:bg-surface-900 dark:hover:border-primary-700 dark:focus-visible:ring-offset-surface-900"
          @click="router.push({ name: shortcut.route })"
        >
          <span class="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600 transition-colors group-hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:group-hover:bg-primary-900/50">
            <i
              :class="shortcut.icon"
              aria-hidden="true"
            />
          </span>
          <span class="text-sm font-medium text-surface-900 dark:text-surface-100">
            {{ shortcut.label }}
          </span>
        </button>
      </div>
    </section>
  </div>
</template>
